// Card fields share a save; comments and fulfillment editors have their own actions.
export default {
    data: () => ({
        changesBusy: false, changesSaved: false, showChangesDialog: false,
        changesIntent: 'leave', changesDialogError: '', separateDrafts: false,
        changesRefreshRequired: false
    }),
    computed: {
        changesSummary() { return this.$store.getters['requestCard/pendingChangesSummary'] || []; }
    },
    beforeRouteLeave(to, from, next) { this.guardRequestNavigation(to, next); },
    beforeRouteUpdate(to, from, next) {
        if (to.params.id === from.params.id) next();
        else this.guardRequestNavigation(to, next);
    },
    mounted() { window.addEventListener('beforeunload', this.warnBeforeRequestUnload); },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.warnBeforeRequestUnload);
        this.finishChangesDialog(false);
    },
    methods: {
        stopDraftTimer() {
            clearTimeout(this.$store.state.requestCard.autoSaveTimer);
            this.$store.commit('requestCard/setAutoSaveTimer', null);
        },
        hasSeparateRequestDrafts() {
            const comments = this.$refs.commentsBlock;
            const materials = this.$refs.materialsTable;
            const actions = this.$refs.requestActions;
            return Boolean((comments && comments.hasDirtyState()) ||
                (materials && (materials.isEditing || materials.hasChanges || materials.showFuelCardLimitsModal)) ||
                (actions && actions.hasDirtyState()));
        },
        warnBeforeRequestUnload(event) {
            if (!this.hasFormChanges && !this.changesBusy && !this.savingRequest && !this.hasSeparateRequestDrafts()) return;
            event.preventDefault();
            event.returnValue = '';
        },
        async guardRequestNavigation(to, next) {
            // An expired session must still be able to reach the login screen.
            if (this.$store.getters.isAuthorized === false && to.name === 'login') { next(); return; }
            if (this.leaveSavedRequest) { this.leaveSavedRequest = false; next(); return; }
            if (this.changesBusy || this.savingRequest) {
                this.requestSaveError = 'Дождитесь завершения сохранения заявки.';
                next(false);
                return;
            }
            if (!this.hasFormChanges && !this.hasSeparateRequestDrafts()) { next(); return; }
            next(await this.askAboutRequestChanges('leave'));
        },
        askAboutRequestChanges(intent) {
            if (this.changesResolver) return Promise.resolve(false);
            this.changesIntent = intent;
            this.changesDialogError = '';
            this.separateDrafts = intent === 'leave' && this.hasSeparateRequestDrafts();
            this.showChangesDialog = true;
            return new Promise(resolve => { this.changesResolver = resolve; });
        },
        finishChangesDialog(proceed) {
            this.showChangesDialog = false;
            if (this.changesResolver) {
                const resolve = this.changesResolver;
                this.changesResolver = null;
                resolve(proceed);
            }
        },
        async resolveChangesDialog(save) {
            if (this.changesBusy || this.savingRequest) return;
            if (save && this.separateDrafts) return;
            this.changesDialogError = '';
            // Leaving discards local values by unmounting. Never writes or publishes a comment.
            if (!save && this.changesIntent === 'leave') {
                this.stopDraftTimer();
                this.finishChangesDialog(true);
                return;
            }
            const ok = save ? await this.saveCardChanges() : await this.discardCardChanges();
            if (ok) this.finishChangesDialog(true);
            else this.changesDialogError = this.requestSaveError;
        },
        async saveCardChanges() {
            if (this.changesBusy || this.savingRequest) return false;
            if (!this.hasFormChanges) return !this.changesRefreshRequired;
            this.changesBusy = true;
            this.requestSaveError = '';
            this.changesSaved = false;
            this.stopDraftTimer();
            try {
                const result = await this.$store.dispatch('requestCard/requestCard_saveRequest');
                if (result && result.lostAccess) {
                    this.finishChangesDialog(false);
                    this.leaveSavedRequest = true;
                    this.$bvToast.toast('Изменения сохранены. После смены объекта доступ к заявке изменился.',
                        {title: 'Заявка сохранена', variant: 'success', solid: true});
                    // Finish a pending route guard before starting the mandatory redirect.
                    await this.$nextTick();
                    await this.$router.push('/requests');
                    return false;
                }
                if (result && result.refreshFailed) {
                    this.changesRefreshRequired = true;
                    this.requestSaveError = result.message;
                    return false;
                }
                await this.onRequestSaved({refreshed: true});
                this.requestSaveError = '';
                this.changesSaved = true;
                this.changesRefreshRequired = false;
                return true;
            } catch (error) {
                this.requestSaveError = (error.response && error.response.data && error.response.data.message) || error.message || 'Не удалось сохранить заявку';
                return false;
            } finally { this.changesBusy = false; }
        },
        async discardCardChanges() {
            this.changesBusy = true;
            this.requestSaveError = '';
            this.stopDraftTimer();
            try {
                await this.$store.dispatch('requestCard/requestCard_discardChanges');
                await this.onRequestSaved({refreshed: true});
                this.requestSaveError = '';
                this.changesSaved = false;
                this.changesRefreshRequired = false;
                return true;
            } catch (error) {
                this.requestSaveError = 'Не удалось загрузить сохранённую заявку. Ваши правки оставлены в форме. ' +
                    ((error.response && error.response.data && error.response.data.message) || error.message || 'Повторите попытку.');
                return false;
            } finally { this.changesBusy = false; }
        },
        async beforeRequestAction(actionKey) {
            if (this.changesBusy || this.savingRequest) return false;
            if (this.changesRefreshRequired) {
                this.requestSaveError = 'Обновите карточку перед продолжением: данные и права после сохранения ещё не получены.';
                return false;
            }
            if (this.hasFormChanges && !await this.askAboutRequestChanges('action')) return false;
            // Save/discard reloads the permission projection; the old button may no longer exist.
            const actions = this.$store.getters['requestCard/actionButtons'] || [];
            if (actionKey && !actions.some(action => action.key === actionKey && !action.disabled)) {
                this.requestSaveError = 'После обновления заявки это действие недоступно. Проверьте её статус и участников.';
                return false;
            }
            return true;
        }
    }
};
